import { drugAction } from '@api'
import { dosageFormOptions, drugRouteOptions } from '@data'
import { FrequencySelect } from '@doctor/Encounter/Manage Orders/Modals/MedicationFields'
import { useDrugs, useMedication } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { frequencySchema, medicationSchema } from '@schema/pDirectory.schema'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { hideMedicationFrequency, showMedicationFrequency } from '@store/slicers/ModalSlicer'
import { palette } from '@themes/palette'
import { FrequencyData } from '@type/medication.type'
import {
  DateInputField,
  FormButton,
  FormInputField,
  Input,
  Modal,
  ModalHeader,
  RadioBtn,
  SearchInput,
  SelectInputField,
  TextAreaField,
  TimeInputField,
  ToggleBtn
} from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { capitalizeDrugName, getError, normalizeHourlyFrequency, removeEmptyValues, showToast } from '@util'
import { useCustomOptionStore } from '@zustore/store'
import { Box, CheckBox, Form, Layer, Text } from 'grommet'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FaPlus } from 'react-icons/fa'
import { useParams, useSearchParams } from 'react-router-dom'
import { InferType } from 'yup'

export const renderFrequencyOptions = (frequencyData: FrequencyData): string => {
  if (!frequencyData) {
    return ''
  }

  const { interval = '', periods = [], type = '' } = frequencyData

  if (type === 'hourly') {
    return interval
  } else if (type === 'daily') {
    if (periods && periods?.length > 0) {
      const formattedPeriods =
        periods?.length > 2
          ? periods.slice(0, -1).join(', ') + ', and ' + periods[periods?.length - 1]
          : periods.join(' and ')
      return `Every ${formattedPeriods}`
    } else {
      return 'Daily'
    }
  }
  return ''
}

export const modules = {
  'emoji-shortname': true,
  'emoji-textarea': false,
  'emoji-toolbar': true,
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: true
  },
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ align: 'justify' }, { align: 'center' }, { align: 'right' }],
      [{ list: 'ordered' }],
      ['link', 'image']
    ]
  }
}

interface Props {
  onClose: () => void
}

export const AddNurseMed = ({ onClose: handleClose }: Props) => {
  const { patientId } = useParams<{ patientId: string }>()
  const { encounterId, intakeId: id } = useParams<string>()
  const { user } = useAppSelector(state => state.user)
  const [durgSearchText] = useState('')
  const { drugs } = useDrugs({ query: durgSearchText })
  const { dosage_unit } = useAppSelector(state => state.config)

  const [searchParams] = useSearchParams()
  const intakeId = searchParams.get('intake') || id

  const { custom_frequency } = useAppSelector(state => state.modal.medication)
  const methods = useForm({
    resolver: yupResolver(medicationSchema)
  })
  const {
    watch,
    setValue,
    formState: { isSubmitting, errors }
  } = methods
  const freq_methods = useForm({ resolver: yupResolver(frequencySchema) })
  const dispatch = useAppDispatch()
  const { createMedication, medFrequencies } = useMedication({})
  const [freq_data, setFreqData] = useState<FrequencyData | null>(null)
  const [, setIsPeriodOfDay] = useState(false)
  const [isHourly, setIsHourly] = useState(true)
  const [setIsEnabled] = useState(true)
  const { isCustomFrequencySelected, setIsCustomFrequencySelected } = useCustomOptionStore()
  const [customOptions, setCustomOptions] = useState<any[]>([])
  const [selectedFrequency, setSelectedFrequency] = useState<string>('')
  const [doseUnit, setDoseUnit] = useState('mg')

  const { drugSearch, createDrug } = useDrugs({ query: watch('nf_drug_name'), form: watch('form') })

  const availableStrengthOptions = useMemo(() => {
    if (!drugSearch || drugSearch?.length === 0) {
      return [...customOptions] // still expose any custom options
    }

    const baseOptions =
      drugSearch.flatMap(
        drug =>
          drug.available_strengths?.map(combination => {
            // 1. Create the user-friendly label by joining the strengths
            const labels = combination?.map(s => s.strength)
            const combinedLabel = labels.join('/')

            // 2. The value will be the original combination array of objects
            return {
              label: combinedLabel,
              value: combinedLabel
            }
          }) ?? []
      ) ?? []

    // merge created custom strengths so they show up in the dropdown
    return [...baseOptions, ...customOptions]
  }, [drugSearch, customOptions])

  const strengthValue = watch('strength')
  const formValue = watch('form')

  const strengthVal = useMemo(() => {
    if (strengthValue) {
      return { label: strengthValue, value: strengthValue }
    } else {
      return { label: '', value: 'strengthValue' }
    }
  }, [strengthValue])

  const [toggleStates, setToggleStates] = useState({
    Period: false,
    Specific_time: false,
    once: false
  })

  const DrugValue = watch('nf_drug_name')
  const value = useMemo(() => {
    if (DrugValue) {
      return { label: capitalizeDrugName(DrugValue), value: DrugValue }
    } else {
      return { label: '', value: 'DrugValue' }
    }
  }, [DrugValue])

  const handleToggle = (id: string, state: boolean) => {
    setToggleStates(prevState => {
      const newToggles = { ...prevState }
      if (id === 'Period') {
        newToggles.Period = state
        if (state) {
          newToggles.Specific_time = false
        }
      } else if (id === 'Specific_time') {
        newToggles.Specific_time = state
        if (state) {
          newToggles.Period = false
        }
      }
      return newToggles
    })
  }

  const handleHourlyCheck = () => {
    setToggleStates({
      Period: false,
      Specific_time: false,
      once: toggleStates.once
    })
    setIsHourly(true)
    setIsPeriodOfDay(false)

    freq_methods.setValue('periods', [])
    freq_methods.setValue('specific_times', [])
  }

  const handleDailyCheck = () => {
    setToggleStates({
      Period: true,
      Specific_time: false,
      once: toggleStates.once
    })
    setIsHourly(false)
    setIsPeriodOfDay(true)

    freq_methods.setValue('interval', '')
  }

  const watchData = freq_methods.watch(['type', 'interval', 'periods', 'specific_times'])

  const set = useMemo(() => {
    const [type, interval, specific_times, periods] = watchData
    if (type === 'hourly') {
      return !interval
    } else if (type === 'daily') {
      return (!specific_times || specific_times?.length === 0) && (!periods || periods?.length === 0)
    }
    return true
  }, [watchData])

  const drug = watch('nf_drug_name')

  useEffect(() => {
    if (drug) {
      setValue('title', drugs?.find(({ id }) => id === drug)?.attributes.name)
    }
  }, [drug, setValue])

  const onFrequencyModalClose = () => {
    dispatch(hideMedicationFrequency())
  }

  const onFreqSubmit = (data: any) => {
    // Normalize the interval if  hourly frequency
    if (data.type === 'hourly' && data.interval) {
      const normalized = normalizeHourlyFrequency(data.interval)
      if (normalized) {
        data.interval = normalized
      }
    }
    setFreqData(data)
    setSelectedFrequency(renderFrequencyOptions(data))
    setIsCustomFrequencySelected(true)
    onFrequencyModalClose()
  }

  const [timeFields, setTimeFields] = useState<string[]>([''])

  const addTimeField = () => {
    setTimeFields([...timeFields, `specific_times_${timeFields?.length}`])
  }

  const onSubmit = async (data: InferType<typeof medicationSchema>) => {
    const {
      dose_quantity,
      icd_codings_attributes,
      allergy_end_date,
      allergy_start_date,
      reaction,
      status,
      ...attributes
    } = data
    delete (attributes as any).api_doses

    const customFrequency = isCustomFrequencySelected ? removeEmptyValues(freq_data!) : {}
    const frequencyId = isCustomFrequencySelected
      ? null
      : medFrequencies!.find(freq => freq.attributes.name === selectedFrequency)?.id
    const relationships: any = {
      patient: {
        data: {
          id: patientId,
          type: 'patient'
        }
      },
      staff: {
        data: {
          id: `${user?.id}`,
          type: 'staff'
        }
      },
      // drug: {
      //   data: {
      //   }
      // },
      ...(frequencyId
        ? {
            medication_frequency: {
              data: {
                id: frequencyId,
                type: 'medication_frequency'
              }
            }
          }
        : {})
    }
    if (intakeId) {
      relationships.intake = {
        data: {
          id: intakeId,
          type: 'intake'
        }
      }
    } else {
      relationships.encounter = {
        data: {
          id: encounterId,
          type: 'encounter'
        }
      }
    }
    const payload = {
      data: {
        attributes: {
          ...attributes,
          ...(intakeId && { reconciliation: true }),
          context: 'intake',
          custom_frequency: customFrequency,
          dosage_unit: doseUnit,
          dose_quantity,
          ordered: true,
          prescribed: false,
          // reconciliation: intakeId ? true : false,
          ...(icd_codings_attributes &&
            icd_codings_attributes?.length > 0 && {
              allergy_attributes: {
                end_date: allergy_end_date,
                icd_codings_attributes,
                patient_id: patientId,
                reaction,
                start_date: allergy_start_date,
                status,
                title: attributes.nf_drug_name
              }
            })
        },
        relationships,
        type: 'medication'
      }
    }

    try {
      await createMedication(payload)
      showToast('Medication added successfully')
      handleClose()
    } catch (error) {
      getError(error)
    }
  }

  const handleCustomSelect = () => {
    dispatch(showMedicationFrequency())
  }

  const handleOptionSelect = (value: string) => {
    setSelectedFrequency(value)
  }

  const clearFrequency = () => {
    setSelectedFrequency('')
    setFreqData(null)
    setIsCustomFrequencySelected(false)
  }

  return (
    <>
      <FormProvider {...methods}>
        <Modal onClose={handleClose} title="Add Medication">
          <Form
            onSubmit={methods.handleSubmit(onSubmit)}
            style={{ display: 'flex', flexDirection: 'column', maxWidth: '550px', overflow: 'auto' }}
          >
            <Box overflow={'auto'} pad={'24px'}>
              <Box flex={false} gap={'12px'}>
                <Controller
                  control={methods.control}
                  name="nf_drug_name"
                  render={({ field: { onChange } }) => (
                    <Select
                      borderRadius={'25px'}
                      defaultOptions
                      getOptionValue={({ value }) => value}
                      isAsync
                      isCreatable
                      label="Drug name"
                      loadOptions={drugAction}
                      onChange={(val: any) => onChange(val?.value)}
                      value={value}
                    />
                  )}
                />
                <SelectInputField
                  className="capitalize"
                  label="Form"
                  name="form"
                  options={dosageFormOptions || []}
                  placeholder="Choose dosage form"
                />

                <Controller
                  control={methods.control}
                  name="strength"
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    const selectedOption = value
                      ? availableStrengthOptions.find(option => option?.value === value) || { label: value, value }
                      : null

                    const handleCreateDrug = async (value: string) => {
                      if (!DrugValue || !formValue) {
                        showToast('Please select drug name and drug form before creating a custom strength')
                        return
                      }

                      // Regex to validate format like "100mg", "10.5ml", or "100mg + 200mg"
                      const strengthRegex =
                        /^\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+(?:\s*\+\s*\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+)*$/
                      if (!strengthRegex.test(value)) {
                        showToast('Invalid format. Use "100mg" or "100mg + 200mg"')
                        return
                      }

                      const payload = {
                        data: {
                          type: 'drug',
                          attributes: {
                            name: DrugValue,
                            form: formValue,
                            strength: value
                          }
                        }
                      }
                      try {
                        await createDrug(payload)
                        setCustomOptions(prev => [...prev, { label: value, value }])
                        onChange(value)
                      } catch (error) {
                        getError(error)
                      }
                    }

                    const handleChange = (newValue: any) => {
                      if (!newValue) {
                        onChange(null) // Handle clearing the select
                        return
                      }

                      if (typeof newValue === 'string') {
                        onChange(newValue)
                      } else if (newValue && newValue?.value) {
                        onChange(newValue?.value)
                      }
                    }

                    return (
                      <Select
                        borderRadius={'25px'}
                        createOptionPosition="first"
                        defaultOptions
                        error={error?.message}
                        getOptionValue={(option: any) => option.value}
                        isCreatable
                        label="Available Strength(s) / Calculated Dosage"
                        onChange={handleChange}
                        onCreateOption={handleCreateDrug}
                        options={availableStrengthOptions}
                        placeholder="eg 100mg + 200mg or 500mg"
                        value={selectedOption}
                      />
                    )
                  }}
                />

                <Box>
                  <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
                    Dosage (Form Based)
                  </Text>
                  <Box
                    align="center"
                    border={{ color: '#999999', side: 'all' }}
                    direction="row"
                    round={'100px'}
                    width={'100%'}
                  >
                    <Controller
                      control={methods.control}
                      name="dose_quantity"
                      render={({ field: { value, onChange, onBlur, name, ref } }) => (
                        <Input
                          name={name}
                          onBlur={onBlur}
                          onChange={e => onChange(e.target.value)}
                          placeholder="Enter dosage"
                          ref={ref}
                          style={{ border: '0', height: '100%', padding: '', width: '342px' }}
                          type="number"
                          value={value == null ? '' : String(value)}
                        />
                      )}
                    />

                    <Box className="borderless !items-center !border-none" direction="row">
                      <div className="borderless h-8"></div>
                      <SearchInput
                        dropProps={
                          {
                            // className: '!left-[407.667px] !w-[420.1px] '
                          }
                        }
                        onChange={val => setDoseUnit(val?.value)}
                        options={dosage_unit.filter(unit => unit !== 'ml') || []}
                        placeholder="unit"
                        setValue={setDoseUnit}
                        style={{ border: 'none', outline: 0, padding: '', textTransform: 'lowercase' }}
                        value={doseUnit}
                      />
                    </Box>
                  </Box>
                </Box>
                <SelectInputField
                  className="capitalize"
                  label="Route"
                  name="route"
                  options={drugRouteOptions || []}
                  placeholder="Choose route"
                />
                <Box className="grid gap-y-2">
                  <Box>
                    <FrequencySelect
                      label="Select a frequency"
                      onClear={clearFrequency}
                      onCustom={handleCustomSelect}
                      onSelect={handleOptionSelect}
                      value={selectedFrequency}
                    />
                  </Box>
                  <Controller
                    control={methods.control}
                    defaultValue={false}
                    name="once"
                    render={({ field }) => (
                      <Box align="center" direction="row" gap="small">
                        <CheckBox
                          checked={field?.value}
                          label="Once"
                          onChange={e => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      </Box>
                    )}
                  />
                </Box>
                <Box className="">
                  <SelectInputField
                    label="Urgency"
                    name="medication_type"
                    options={['STAT', 'Routine']}
                    placeholder="Select preferred regimen"
                  />
                </Box>
                <div>
                  <DateInputField
                    className=""
                    dateFormat="MMMM d, yyyy HH:mm"
                    label="Start Date and Time"
                    name="start_at"
                    showTimeSelect
                  />
                </div>
                <div>
                  <DateInputField
                    dateFormat="MMMM d, yyyy HH:mm"
                    label="End Date and Time"
                    name="end_at"
                    showTimeSelect
                  />
                </div>
                <TextAreaField label="Note (optional)" name="comment" placeholder="Enter note" />
              </Box>
            </Box>

            <FormButton isLoading={isSubmitting} onClose={handleClose} submit={'Add'} />
          </Form>
        </Modal>
      </FormProvider>
      {custom_frequency.add && (
        <FormProvider {...freq_methods}>
          <Layer
            className=""
            modal
            onClickOutside={onFrequencyModalClose}
            onEsc={onFrequencyModalClose}
            position="center"
            style={{ borderRadius: '15px' }}
          >
            <Form onSubmit={freq_methods.handleSubmit(onFreqSubmit)}>
              <Box style={{ maxHeight: '80vh', width: 'min(500px, calc(100vw - 1.5rem))' }}>
                <ModalHeader onClose={onFrequencyModalClose} title="Set Frequency" />
                <div className="grid gap-y-[1.7em] overflow-auto  p-6">
                  <Box className="">
                    <Controller
                      control={freq_methods.control}
                      defaultValue="hourly"
                      name="type"
                      render={({ field }) => (
                        <>
                          <Box margin={{ top: '12px' }}>
                            <Box align="center" direction="row" gap={'12px'} id={'hourly'} pad={{ bottom: '12px' }}>
                              <RadioBtn
                                {...field}
                                checked={'hourly' === field?.value}
                                onChange={() => {
                                  field.onChange('hourly')
                                  setIsHourly(true)
                                  setIsPeriodOfDay(false)
                                  handleHourlyCheck()
                                }}
                                value={'hourly'}
                              />
                              <Text
                                color={isHourly ? '#1A1A1A' : '#3D3D3D'}
                                size="small"
                                style={{ textTransform: 'capitalize' }}
                              >
                                {'Hourly'}
                              </Text>
                            </Box>
                          </Box>
                        </>
                      )}
                    />
                    <Box className="">
                      <FormInputField
                        disabled={!isHourly}
                        label="Intervals"
                        labelClassName={`${!isHourly ? '!text-[#1A1A1A] !opacity-50' : '!text-[#3D3D3D]'} `}
                        name="interval"
                        placeholder="E.g every 5 hours"
                        style={{ color: isHourly ? '#1A1A1A' : '#3D3D3D', opacity: isHourly ? 1 : 0.5 }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Controller
                      control={freq_methods.control}
                      name="type"
                      render={({ field }) => (
                        <>
                          <Box className="!min-h-[auto]" margin={{ top: '12px' }}>
                            <Box align="center" direction="row" gap={'12px'} id={'daily'} pad={{ bottom: '12px' }}>
                              <RadioBtn
                                {...field}
                                checked={'daily' === field?.value}
                                onChange={() => {
                                  field.onChange('daily')
                                  setIsHourly(false)
                                  setIsPeriodOfDay(true)
                                  handleDailyCheck()
                                }}
                                value={'daily'}
                              />
                              <Text
                                color={!isHourly ? '#1A1A1A' : '#3D3D3D'}
                                size="small"
                                style={{ textTransform: 'capitalize' }}
                              >
                                {'Daily'}
                              </Text>
                            </Box>
                          </Box>
                        </>
                      )}
                    />
                    <Box
                      border={{ color: '#999999' }}
                      className="!min-h-[auto]"
                      gap={'.9em'}
                      height={'fit-content'}
                      pad={'.9em'}
                      round="8px"
                      style={{ opacity: !isHourly ? 1 : 0.5 }}
                    >
                      <div style={{ opacity: !toggleStates.Specific_time ? 1 : 0.5 }}>
                        <Box alignContent="center" className="justify-between" direction="row">
                          <Text color={!isHourly ? '#1A1A1A' : '#3D3D3D'} size="16px">
                            Period of the day
                          </Text>
                          <ToggleBtn
                            disabled={isHourly}
                            id="Period"
                            isToggledOn={toggleStates.Period}
                            onToggle={handleToggle}
                          />
                        </Box>
                        <Box>
                          <Controller
                            control={freq_methods.control}
                            name="periods"
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Box margin={{ top: '12px' }}>
                                  {['morning', 'afternoon', 'night']?.map(list => (
                                    <Box
                                      align="center"
                                      direction="row"
                                      gap={'12px'}
                                      id={list}
                                      key={list}
                                      pad={{ bottom: '15px' }}
                                    >
                                      <CheckBox
                                        {...field}
                                        checked={field?.value?.includes(list)}
                                        disabled={!toggleStates.Period || isHourly}
                                        onChange={e => {
                                          const newValue = e.target.checked
                                            ? [...(field?.value || []), list]
                                            : field?.value?.filter((val: string | undefined) => val !== list)
                                          field.onChange(newValue)
                                        }}
                                        value={list}
                                      />
                                      <Text
                                        color={!toggleStates.Period || isHourly ? '#3D3D3D' : '#1A1A1A'}
                                        size="small"
                                        style={{ textTransform: 'capitalize' }}
                                      >
                                        {list}
                                      </Text>
                                    </Box>
                                  ))}
                                </Box>
                                <Box pad={{ left: 'small' }}>
                                  <Text color="status-error" size="small">
                                    {error?.message}
                                  </Text>
                                </Box>
                              </>
                            )}
                          />
                        </Box>
                      </div>
                      <Box style={{ opacity: !toggleStates.Period ? 1 : 0.5 }}>
                        <Box alignContent="center" className="justify-between pb-4 text-[16px]" direction="row">
                          <Text color={!toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D'} size="16px">
                            Specific time of the day
                          </Text>
                          <ToggleBtn
                            disabled={isHourly}
                            id="Specific_time"
                            isToggledOn={toggleStates.Specific_time}
                            onToggle={handleToggle}
                          />
                        </Box>
                        <div className="grid gap-y-3">
                          {timeFields?.map((field, index) => (
                            <div className="!h-fit" key={index}>
                              <TimeInputField
                                className="uppercase"
                                disabled={!toggleStates.Specific_time || isHourly}
                                label={``}
                                name={`specific_times.${index}`}
                                style={{
                                  color: toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D',
                                  opacity: toggleStates.Specific_time ? 1 : 0.5
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-x-[5px] items-center mt-2" onClick={addTimeField}>
                          <FaPlus color={palette.brand} />
                          <Text color={palette.brand} size="12px">
                            Add time
                          </Text>
                        </div>
                      </Box>
                    </Box>
                  </Box>
                </div>
                <FormButton disabled={!setIsEnabled} onClose={onFrequencyModalClose} submit="Set" type="submit" />
              </Box>
            </Form>
          </Layer>
        </FormProvider>
      )}
    </>
  )
}
